import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import UploadImage from "../Components/common/UploadImage";

export default function ProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [sort, setSort] = useState("name");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setStatus("loading");
      try {
        // Categories are secondary; a failure there should not blank the page.
        const [programRes, categoryRes] = await Promise.all([
          api.get("/program/getAllprograms"),
          api.get("/category/allcategories").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;

        const list = programRes.data?.programs || programRes.data?.data || [];
        const cats = Array.isArray(categoryRes.data)
          ? categoryRes.data
          : categoryRes.data?.data || [];

        setPrograms(list);
        setCategories(cats);
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setError(
          err.response?.data?.message ||
            "We couldn't load the programs. Please try again."
        );
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const categoryName = useMemo(() => {
    const map = new Map(categories.map((c) => [String(c._id), c.name]));
    return (id) => map.get(String(id?._id || id)) || "Uncategorised";
  }, [categories]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();

    let list = programs.filter((p) => {
      const inCategory =
        categoryId === "all" || String(p.category?._id || p.category) === categoryId;
      const matches =
        !term ||
        p.name?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term);
      return inCategory && matches;
    });

    list = [...list].sort((a, b) => {
      if (sort === "price-asc") return (a.price || 0) - (b.price || 0);
      if (sort === "price-desc") return (b.price || 0) - (a.price || 0);
      return (a.name || "").localeCompare(b.name || "");
    });

    return list;
  }, [programs, search, categoryId, sort]);

  return (
    <div className="es-page">
      <p className="es-eyebrow">What we offer</p>
      <h1 className="es-h1">Programs and services</h1>
      <p className="es-lede">
        Specialist programs delivered by our therapy and education team. Every
        placement begins with an assessment, so families can be matched to the
        right level of support.
      </p>

      <div
        style={{
          display: "grid",
          gap: "1rem",
          gridTemplateColumns: "repeat(auto-fit, minmax(12rem, 1fr))",
          marginBottom: "2rem",
        }}
      >
        <div className="es-field" style={{ margin: 0 }}>
          <label className="es-label" htmlFor="program-search">
            Search programs
          </label>
          <input
            id="program-search"
            className="es-input"
            type="search"
            placeholder="e.g. speech, sensory"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="es-field" style={{ margin: 0 }}>
          <label className="es-label" htmlFor="program-category">
            Category
          </label>
          <select
            id="program-category"
            className="es-select"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="es-field" style={{ margin: 0 }}>
          <label className="es-label" htmlFor="program-sort">
            Sort by
          </label>
          <select
            id="program-sort"
            className="es-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="name">Name (A–Z)</option>
            <option value="price-asc">Price (low to high)</option>
            <option value="price-desc">Price (high to low)</option>
          </select>
        </div>
      </div>

      {status === "loading" && (
        <div className="es-grid" aria-busy="true" aria-live="polite">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="es-card">
              <div className="es-skeleton es-skeleton--card" />
              <div className="es-card__body">
                <div className="es-skeleton es-skeleton--text" style={{ width: "70%" }} />
                <div className="es-skeleton es-skeleton--text" />
                <div className="es-skeleton es-skeleton--text" style={{ width: "40%" }} />
              </div>
            </div>
          ))}
          <span className="es-visually-hidden">Loading programs</span>
        </div>
      )}

      {status === "error" && (
        <div className="es-alert es-alert--error" role="alert">
          {error}
        </div>
      )}

      {status === "ready" && visible.length === 0 && (
        <div className="es-state">
          <p className="es-state__title">No programs match your search</p>
          <p>Try a different term, or clear the category filter.</p>
        </div>
      )}

      {status === "ready" && visible.length > 0 && (
        <>
          <p className="es-muted" aria-live="polite" style={{ marginBottom: "1rem" }}>
            Showing {visible.length} of {programs.length} programs
          </p>
          <div className="es-grid">
            {visible.map((program) => (
              <article key={program._id} className="es-card">
                <UploadImage
                  file={program.image}
                  alt=""
                  style={{ width: "100%", height: "9rem", objectFit: "cover", display: "block" }}
                />
                <div className="es-card__body">
                  <span className="es-badge es-badge--neutral">
                    {categoryName(program.category)}
                  </span>
                  <h2 className="es-h3" style={{ marginTop: "0.75rem" }}>
                    {program.name}
                  </h2>
                  <p className="es-body" style={{ fontSize: "0.92rem" }}>
                    {program.description}
                  </p>
                  <p
                    style={{
                      marginTop: "1rem",
                      fontWeight: 700,
                      color: "var(--es-teal-700)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    ${Number(program.price || 0).toLocaleString()}
                    <span className="es-muted" style={{ fontWeight: 400 }}>
                      {" "}
                      · {program.participantsCount || 0} enrolled
                    </span>
                  </p>
                </div>
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
